import { Fragment, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router";

import isNil from "lodash/isNil";
import validator from "shared/dist/utils/validator";
import { submitServiceGroups } from "./validationConstraints";
import { generateDetailedErrorMessageFromValidation } from "../../../Utils/validation";

import VerificationPending from "shared/dist/assets/images/VerificationPending.png";
import { SNETStatusBanner, statusTitleType, AlertBox, alertTypes } from "shared/dist/components";
import { bannerStates } from "./LaunchBanerStates";
import { progressStatus, sections, serviceCreationStatus } from "../constant";
import { aiServiceDetailsActions } from "../../../Services/Redux/actionCreators";
import { storageProviders } from "../../../Utils/storageTypes";
import { isEmpty } from "lodash";
import { useCallback } from "react";
import { checkIsOwnerAddress } from "../../../Utils/Blockchain";
import { useDaemonStatus } from "../../../Components/HaaS/Hooks/useProcessDaemonStatus";
import useSubmit from "../hooks/useSubmit";
import { useNavigateTo } from "../../../GlobalRouter/hook/NavigateTo";
import { Debug } from "../../../data/configuration";

const LaunchService = () => {
    const navigateTo = useNavigateTo();
    const { submitServiceUpdate } = useSubmit();

    const [isLaunchable, setLaunchable] = useState(false);
    const [submitError, setSubmitError] = useState();
    const [validatonErrors, setValidationErrors] = useState();
    const [serviceInfo, setServiceInfo] = useState(bannerStates.unableToPublish);

    const orgId = useSelector((state) => state.organization.id);
    const ownerAddress = useSelector((state) => state.organization.owner.address);
    const serviceDetails = useSelector((state) => state.aiServiceDetails);

    const { orgUuid, serviceUuid } = useParams();
    const { searchDaemons, pollDaemonStatus } = useDaemonStatus();
    const dispatch = useDispatch();

    const getIsDataValid = useCallback(
        (aiServiceDetails) => {
            const isGroupValid = handleValidation(aiServiceDetails.groups[0], submitServiceGroups);
            const isFilecoinTokenExist =
                serviceDetails.storageProvider !== storageProviders.FILECOIN ||
                !isEmpty(serviceDetails.lighthouseToken);
            if (!isFilecoinTokenExist) {
                setSubmitError("Please, fill the lighthouse token on AI Service Profile tab!");
            }

            return isGroupValid && isFilecoinTokenExist;
        },
        [serviceDetails.lighthouseToken, serviceDetails.storageProvider]
    );

    const isDemoComponentLaunchable = useCallback((demoComponentAvailable, demoFileBuildStatus) => {
        return (
            !demoComponentAvailable ||
            (demoFileBuildStatus === progressStatus.SUCCEEDED && demoComponentAvailable)
        );
    }, []);

    useEffect(() => {
        const checkServiceStatus = async () => {
            const aiServiceDetails = await dispatch(
                aiServiceDetailsActions.getServiceDetails(orgUuid, serviceUuid, orgId)
            );

            if (!aiServiceDetails.foundInBlockchain) {
                const existDaemon = await searchDaemons(orgId, serviceDetails.id);
                await pollDaemonStatus(existDaemon, orgId, serviceDetails.id);
            }

            const { assets, serviceState } = aiServiceDetails;

            const demoComponentAvailable = Boolean(assets.demoFiles.required);
            const demoFileBuildStatus = assets.demoFiles.status?.toLowerCase();
            const protoFileBuildStatus = assets.protoFiles.status?.toLowerCase();
            const serviceStatus = serviceState.state;

            if (
                isNil(protoFileBuildStatus) ||
                (demoComponentAvailable && isNil(demoFileBuildStatus))
            ) {
                dispatch(
                    aiServiceDetailsActions.updateProgressStatus(
                        sections.LAUNCH,
                        progressStatus.FAILED,
                        serviceDetails.progressStages
                    )
                );
            }

            let serviceStatusSection = bannerStates.readyToLaunch;
            const isDataValid = getIsDataValid(aiServiceDetails);
            if (!isDataValid) {
                serviceStatusSection = bannerStates.unableToPublish;
            } else if (
                isDemoComponentLaunchable(demoComponentAvailable, demoFileBuildStatus) &&
                protoFileBuildStatus === progressStatus.SUCCEEDED &&
                serviceState.state === serviceCreationStatus.APPROVED
            ) {
                setLaunchable(true);
            } else if (
                demoFileBuildStatus === progressStatus.PENDING ||
                protoFileBuildStatus === progressStatus.PENDING
            ) {
                serviceStatusSection = bannerStates.unableToPublishBuildInProgress;
            } else if (serviceStatus === serviceCreationStatus.PUBLISH_IN_PROGRESS) {
                serviceStatusSection = bannerStates.publishInProgress;
            } else if (serviceStatus === serviceCreationStatus.PUBLISHED) {
                serviceStatusSection = bannerStates.alreadyPublished;
            } else {
                serviceStatusSection = bannerStates.unableToPublish;
            }

            setServiceInfo(serviceStatusSection);
        };
        checkServiceStatus();
    }, [
        dispatch,
        getIsDataValid,
        pollDaemonStatus,
        isDemoComponentLaunchable,
        orgId,
        orgUuid,
        serviceUuid,
        serviceDetails.id,
        serviceDetails.progressStages,
        searchDaemons,
    ]);

    const handleValidation = (fieldsForValidation, validationConstraints) => {
        const invalidFieldsMessages = validator(fieldsForValidation, validationConstraints);
        if (invalidFieldsMessages) {
            const generatedErrorsMessage =
                generateDetailedErrorMessageFromValidation(invalidFieldsMessages);
            setValidationErrors(generatedErrorsMessage);
            setLaunchable(false);
            return false;
        }
        setValidationErrors();
        return true;
    };

    const handleBackToDashboard = () => {
        dispatch(aiServiceDetailsActions.setCreateNewAiService(true));
        navigateTo.services({ orgUuid });
    };

    const submitPublish = async () => {
        try {
            !Debug.skipCheckOwnerAddress && (await checkIsOwnerAddress(ownerAddress));
            await submitServiceUpdate();
        } catch (error) {
            setSubmitError(error.message);
        }
    };

    return (
        <Fragment>
            <SNETStatusBanner
                title={serviceInfo.title}
                img={serviceInfo.image || VerificationPending}
                description={serviceInfo.description}
                actions={[
                    {
                        children: "launch ai service",
                        variant: "contained",
                        color: "primary",
                        onClick: submitPublish,
                        disabled:
                            !isLaunchable ||
                            serviceDetails.serviceState.state === serviceCreationStatus.PUBLISHED,
                    },
                    {
                        children: "back to dashboard",
                        variant: "outlined",
                        color: "primary",
                        onClick: handleBackToDashboard,
                    },
                ]}
                type={statusTitleType.PENDING}
            />
            {validatonErrors && <AlertBox children={validatonErrors} type={alertTypes.ERROR} />}
            {submitError && <AlertBox message={submitError} type={alertTypes.ERROR} />}
        </Fragment>
    );
};

export default LaunchService;
